// root '../../../';

// dependencies
import { Child } from 'pym.js';
import * as d3 from 'd3';
import Zion from './utils/Zion';

// polyfills
// import fetch from 'unfetch';
import './utils/polyfills'; // (includes Array.findIndex && Array.find)

// utils & graphic
import { stylePremium } from './utils/helpers';
import config from './config';
import graphic from './graphic';

// styles
import './styles/graphic.scss';

/* develblock:start */
import meta from './data/meta.json';

const pkg = require('../package.json');

if (process.env.NODE_ENV === 'development') {
  console.clear();
}

d3.timeFormatDefaultLocale({
  periods: ['a.m.', 'p.m.'],
  days: [
    'Sunday',
    'Monday',
    'Tuesday',
    'Wednesday',
    'Thursday',
    'Friday',
    'Saturday',
  ],
  shortDays: ['Sun', 'Mon', 'Tues', 'Wed', 'Thurs', 'Fri', 'Sat'],
  months: [
    'January',
    'February',
    'March',
    'April',
    'May',
    'June',
    'July',
    'August',
    'September',
    'October',
    'November',
    'December',
  ],
  shortMonths: [
    'Jan.',
    'Feb.',
    'March',
    'April',
    'May',
    'June',
    'July',
    'Aug.',
    'Sept.',
    'Oct.',
    'Nov.',
    'Dec.',
  ],
});

let chartData;
let rawData;
let currentData;
let lastData;

const margins = {
  top: 20,
  right: 145,
  bottom: 25,
  left: 40,
};

let minValue;
let maxValue;

let minDomain;
let maxDomain;

let brentStats = null;

// eslint-disable-next-line  no-use-before-define
window.pymChild = new Child({ id: pkg.name });

window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  if (brentStats) {
    updateDekAndFootnote();
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }
};

const CNNPolitics = false;
const CNNBusiness = false;

const BRENT_LIVE_URL = 'https://production.dataviz.cnn.io/charting/instruments/latest/list/Commodity/BRN00';
const BRENT_SETTLE_URL = 'https://ix.cnn.io/data/oil-prices/brent.json';

// Freeze the chart to a point in time so a story published on a given day keeps
// showing that day's view even after a week of new settles and live ticks.
//
//   ?asof=YYYY-MM-DD   clamp the settle series and window to this date, live off
//   ?price=100.58      pin the intraday endpoint value the reader saw (optional)
//   ?change=2.66       override the change vs the previous settle (optional)
//   ?time=<ISO>        timestamp for the "as of … ET" label (optional)
//
// Add ?freeze to the live chart to get a "Copy frozen link" button that bakes
// the current live values into that URL. The button only appears with ?freeze,
// so it never renders in a published embed.
const urlParams = new URLSearchParams(window.location.search);

const FROZEN = (() => {
  const asofStr = urlParams.get('asof');
  if (!asofStr) return null;
  const asof = d3.timeParse('%Y-%m-%d')(asofStr);
  if (!asof) return null;
  asof.setHours(23, 59, 59, 999); // inclusive of the whole as-of day
  const price = parseFloat(urlParams.get('price'));
  const change = parseFloat(urlParams.get('change'));
  return {
    asof,
    price: Number.isFinite(price) ? price : null,
    change: Number.isFinite(change) ? change : null,
    time: urlParams.get('time') || null,
  };
})();

const FREEZE_GENERATOR = urlParams.has('freeze') && !FROZEN;

stylePremium(CNNPolitics, CNNBusiness);

let livePrice = null;

function formatLongDate(isoDate) {
  const d = d3.timeParse('%Y-%m-%d')(isoDate);
  if (!d) return isoDate;
  return `${d3.timeFormat('%B')(d)} ${d.getDate()}, ${d.getFullYear()}`;
}

function formatShortDate(isoDate) {
  const d = d3.timeParse('%Y-%m-%d')(isoDate);
  if (!d) return isoDate;
  return `${d3.timeFormat('%b')(d)} ${d.getDate()}, ${d.getFullYear()}`;
}

function referenceSettle(beforeDate) {
  // Most recent settled point with a date strictly before `beforeDate`.
  // rawData is sorted ascending, so walk until we hit/pass the target.
  if (!rawData || !rawData.length) return null;
  if (!beforeDate) return rawData[rawData.length - 1];

  const target = typeof beforeDate === 'string'
    ? d3.timeParse('%Y-%m-%d')(beforeDate)
    : beforeDate;

  let ref = null;
  for (let i = 0; i < rawData.length; i += 1) {
    if (target && +rawData[i].date >= +target) break;
    ref = rawData[i];
  }
  return ref || (rawData.length > 1 ? rawData[rawData.length - 2] : rawData[0]);
}

function liveChangeBase() {
  // The settle the live price is measured against: the last settle strictly
  // before the live price's own trading date. This keeps "change from the
  // previous settle" correct on non-trading days, when the live price is
  // frozen at the last close and the archive already holds that day's settle.
  if (livePrice && livePrice.date) {
    const ref = referenceSettle(livePrice.date);
    if (ref) return ref.close;
  }
  return brentStats ? brentStats.latestValue : null;
}

// AP-style month abbreviations (March through July are spelled out)
const AP_MONTHS = [
  'Jan.', 'Feb.', 'March', 'April', 'May', 'June',
  'July', 'Aug.', 'Sept.', 'Oct.', 'Nov.', 'Dec.',
];

function formatLiveTimestamp(isoTimestamp) {
  if (!isoTimestamp) return '';
  const d = new Date(isoTimestamp);

  // Resolve the date/time in Eastern time, then apply AP month styling.
  const parts = new Intl.DateTimeFormat('en-US', {
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: 'America/New_York',
  }).formatToParts(d).reduce((acc, p) => {
    acc[p.type] = p.value;
    return acc;
  }, {});

  const month = AP_MONTHS[Number(parts.month) - 1];
  const period = (parts.dayPeriod || '').replace(/AM/i, 'a.m.').replace(/PM/i, 'p.m.');
  return `${month} ${parts.day}, ${parts.hour}:${parts.minute} ${period} ET`;
}

function updateDekAndFootnote() {
  if (!brentStats) return;

  const s = brentStats;

  let displayPrice;
  let displayChange;
  let displayPctChange;
  if (livePrice) {
    displayPrice = livePrice.currentPrice;
    if (livePrice.frozen) {
      displayChange = livePrice.change;
      displayPctChange = livePrice.pctChange * 100;
    } else {
      const base = liveChangeBase();
      displayChange = livePrice.currentPrice - base;
      displayPctChange = (displayChange / base) * 100;
    }
  } else {
    displayPrice = s.latestValue;
    displayChange = s.dailyChange;
    displayPctChange = s.dailyPctChange;
  }

  const required = [displayPrice, displayPctChange];
  if (required.some(v => Number.isNaN(v))) return;

  const direction = displayPctChange >= 0 ? 'up' : 'down';
  const pctStr = `${Math.abs(displayPctChange).toFixed(2)}%`;

  const priceStr = `$${d3.format(',.2f')(displayPrice)}`;
  const pctChangeColor = displayPctChange >= 0 ? '#53A796' : '#c52622';

  const dekHtml = `The latest price of Brent crude — the global oil benchmark — is <strong>${priceStr}</strong> per barrel, ${direction} <strong style="color: ${pctChangeColor};">${pctStr}</strong> from the previous settle price.`;

  const dekPlain = `The latest price of Brent crude — the global oil benchmark — is ${priceStr} per barrel, ${direction} ${pctStr} from the previous settle price.`;

  const dekEl = document.querySelector('.dek');
  if (dekEl) {
    dekEl.innerHTML = dekHtml;
    dekEl.setAttribute('aria-label', dekPlain);
  }

  const footEl = document.querySelector('.footnote');
  if (footEl) {
    let note = 'Note: Historical prices reflect settlement prices for the most actively traded Brent crude oil futures contract at the time on the Intercontinental Exchange.';
    if (FROZEN) {
      const asofLabel = livePrice && livePrice.frozen && livePrice.lastUpdated
        ? formatLiveTimestamp(livePrice.lastUpdated)
        : formatShortDate(brentStats.latestDate);
      note += ` This is a frozen snapshot as of ${asofLabel} and is not updating with live prices.`;
    }
    footEl.textContent = note;
  }

  if (window.pymChild) {
    window.pymChild.sendHeight();
  }
}

function applySettlePayload(settles) {
  const parseDay = d3.timeParse('%Y-%m-%d');

  rawData = settles
    .map(d => ({
      date: parseDay(d.event_date),
      close: +d.current_price,
    }))
    .filter(d => d.date && !Number.isNaN(d.close))
    .sort((a, b) => +a.date - +b.date);

  if (!rawData.length) return;

  // When frozen, only consider settles on or before the as-of date. The window
  // then anchors to the last of those, reproducing the view as of that day.
  const series = FROZEN ? rawData.filter(d => d.date <= FROZEN.asof) : rawData;
  if (!series.length) return;

  const latest = series[series.length - 1];
  const previous = series[series.length - 2];

  currentData = {
    date: latest.date,
    close: latest.close,
    prevClose: previous ? previous.close : latest.close,
    change: previous ? latest.close - previous.close : 0,
    percentChange: previous ? (latest.close - previous.close) / previous.close : 0,
  };

  brentStats = {
    latestDate: d3.timeFormat('%Y-%m-%d')(latest.date),
    latestValue: latest.close,
    previousClose: previous ? previous.close : latest.close,
    dailyChange: currentData.change,
    dailyPctChange: currentData.percentChange * 100,
  };

  // Frozen chart with a pinned intraday price: synthesize the live endpoint so
  // the rest of the render path shows it exactly as the reader saw it.
  if (FROZEN && FROZEN.price != null) {
    const base = latest.close; // the previous settle the intraday price is measured against
    const change = FROZEN.change != null ? FROZEN.change : FROZEN.price - base;
    livePrice = {
      currentPrice: FROZEN.price,
      change,
      pctChange: base ? change / base : 0,
      lastUpdated: FROZEN.time,
      date: d3.timeFormat('%Y-%m-%d')(FROZEN.asof),
      frozen: true,
    };
  }

  const seriesEnd = latest.date;
  const windowStart = d3.timeYear.offset(seriesEnd, -1);
  chartData = series.filter(d => d.date >= windowStart);

  minDomain = d3.min(chartData, d => d.date);
  maxDomain = d3.max([d3.max(chartData, d => d.date), currentData.date]);

  const dataMin = d3.min(chartData, d => d.close);
  const dataMax = d3.max(chartData, d => d.close);
  const span = dataMax - dataMin || 1;
  const pad = span * 0.2;
  minValue = dataMin - pad;
  maxValue = dataMax + pad;

  lastData = chartData[chartData.length - 1];
  updateDekAndFootnote();
}

async function fetchSettleData() {
  try {
    const response = await fetch(`${BRENT_SETTLE_URL}?t=${Date.now()}`);
    const data = await response.json();

    // brent.json is already a settled-only series; map to the shape
    // applySettlePayload expects ({ event_date, current_price }).
    const settles = (data.data || []).map(d => ({
      event_date: d.date,
      current_price: d.price,
    }));

    applySettlePayload(settles);
    draw();
  } catch (error) {
    console.error('Error fetching settle data:', error);
  }
}

async function fetchLivePrice() {
  if (FROZEN) return; // frozen charts never poll the live endpoint
  try {
    const cacheBust = `?t=${Date.now()}`;
    const response = await fetch(BRENT_LIVE_URL + cacheBust);
    const result = await response.json();
    if (result && result.data && result.data[0]) {
      const live = result.data[0];
      const currentPrice = +live.current_price;

      livePrice = {
        currentPrice,
        lastUpdated: live.last_updated,
        date: live.event_date,
      };
      updateDekAndFootnote();
      draw();
    }
  } catch (error) {
    console.error('Error fetching live Brent price:', error);
  }
}

fetchSettleData().then(() => {
  if (!FROZEN) {
    fetchLivePrice();
    setInterval(fetchLivePrice, 30 * 1000);
  }
  draw();
  window.onresize = draw;
});

// Build the frozen-chart URL from the current live values.
function buildFrozenUrl() {
  if (!currentData) return null;
  const params = new URLSearchParams();
  params.set('asof', d3.timeFormat('%Y-%m-%d')(currentData.date));
  if (livePrice) {
    const base = liveChangeBase();
    params.set('price', livePrice.currentPrice.toFixed(2));
    if (base) params.set('change', (livePrice.currentPrice - base).toFixed(2));
    if (livePrice.lastUpdated) params.set('time', livePrice.lastUpdated);
  }
  return `${window.location.origin}${window.location.pathname}?${params.toString()}`;
}

// Exposed so the parent.html preview's "Freeze now" button can read the current
// live values straight from the iframe.
window.buildFrozenUrl = buildFrozenUrl;

// "Copy frozen link" button, shown only when the chart is loaded with ?freeze.
function renderFreezeGenerator() {
  if (!FREEZE_GENERATOR || document.getElementById('freeze-link-btn')) return;

  const btn = document.createElement('button');
  btn.id = 'freeze-link-btn';
  btn.type = 'button';
  btn.textContent = 'Copy frozen link';
  Object.assign(btn.style, {
    position: 'fixed',
    top: '8px',
    right: '8px',
    zIndex: 9999,
    padding: '8px 12px',
    font: '13px/1 "CNN Sans Display", sans-serif',
    background: '#262626',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
  });

  btn.addEventListener('click', () => {
    const url = buildFrozenUrl();
    if (!url) {
      btn.textContent = 'No data yet';
      return;
    }
    if (navigator.clipboard) {
      navigator.clipboard.writeText(url).catch(() => {});
    }
    // Logged as well, so it can be copied even if the clipboard API is blocked.
    console.log(`Frozen chart URL:\n${url}`);
    btn.textContent = 'Copied!';
    setTimeout(() => { btn.textContent = 'Copy frozen link'; }, 1500);
  });

  document.body.appendChild(btn);
}

function draw() {
  if (!chartData || !chartData.length || minDomain == null || maxDomain == null) {
    return;
  }

  renderFreezeGenerator();

  const container = d3.select('#graphic');
  container.html('');

  const initialWidth = container.node().clientWidth;
  const isMobile = initialWidth <= 500;
  const height = 400;

  const chartHeight = height - margins.top - margins.bottom;
  const chartWidth = initialWidth - margins.left - margins.right;

  const yTicks = isMobile ? 6 : 6;

  const xScale = d3.scaleTime()
    .domain([minDomain, maxDomain])
    .range([10, chartWidth]);

  const yScale = d3.scaleLinear()
    .domain([minValue, maxValue])
    .range([chartHeight, 0]);

  const xAxis = d3.axisBottom()
    .scale(xScale)
    .ticks(isMobile ? 2 : 6)
    .tickValues(isMobile ? [minDomain, maxDomain] : null)
    .tickSize(6)
    .tickFormat((d, i, nodes) => {
      if (isMobile) {
        return d3.timeFormat('%b \'%y')(d);
      }
      return d3.timeFormat('%b %Y')(d);
    });

  const yAxis = d3.axisLeft()
    .scale(yScale)
    .ticks(yTicks)
    .tickFormat(d => `$${d3.format(',')(d)}`)
    .tickSize(-chartWidth);

  const path = d3.line()
    .curve(d3.curveMonotoneX)
    .x(d => xScale(d.date))
    .y(d => yScale(d.close));

  const svg = container.append('svg')
    .attr('width', initialWidth)
    .attr('height', height);

  const g = svg.append('g')
    .attr('transform', `translate(${[margins.left, margins.top]})`);

  g.append('g')
    .attr('class', 'grid x')
    .attr('transform', `translate(${[0, chartHeight]})`)
    .call(xAxis);

  g.append('g')
    .attr('class', 'grid y')
    .call(yAxis);

  g.append('line')
    .attr('class', 'x-baseline')
    .attr('x1', 0)
    .attr('x2', chartWidth)
    .attr('y1', chartHeight)
    .attr('y2', chartHeight)
    .attr('stroke', '#b1b1b1')
    .attr('stroke-width', 1);

  const dayKey = d => d3.timeFormat('%Y-%m-%d')(d);
  const combinedData = chartData.slice();

  if (currentData) {
    const last = combinedData[combinedData.length - 1];
    if (last && dayKey(last.date) === dayKey(currentData.date)) {
      combinedData[combinedData.length - 1] = { date: currentData.date, close: currentData.close };
    } else {
      combinedData.push({ date: currentData.date, close: currentData.close });
    }
  }

  if (livePrice && currentData) {
    const liveDate = new Date(currentData.date);
    liveDate.setHours(23, 59, 59);
    const liveDataPoint = { date: liveDate, close: livePrice.currentPrice };
    combinedData.push(liveDataPoint);
  }

  combinedData.sort((a, b) => +a.date - +b.date);

  d3.select('.grid.y .domain').remove();

  const eventDate = d3.timeParse('%Y-%m-%d')('2026-02-27');
  if (eventDate && eventDate >= minDomain && eventDate <= maxDomain) {
    const eventX = xScale(eventDate);

    const eventGroup = g.append('g')
      .attr('class', 'event-marker');

    eventGroup.append('line')
      .attr('class', 'event-line')
      .attr('x1', eventX)
      .attr('x2', eventX)
      .attr('y1', 0)
      .attr('y2', chartHeight)
      .attr('stroke', '#8C8C8C')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '3 3');

    const gapFromLine = 6;
    const edgePad = 4;
    const textY = 58;

    const eventText = eventGroup.append('text')
      .attr('class', 'event-annotation')
      .attr('y', textY);

    // Date-led annotation: bold date over a light description, no value. The
    // description shortens on mobile and drops entirely if it can't clear an
    // axis, so the type never falls below 12px.
    const bodyLines = isMobile ? ['Strikes on', 'Iran begin'] : ['US/Israel strikes', 'against Iran begin'];

    eventText.append('tspan')
      .attr('dy', '-4.2em')
      .attr('class', 'event-date')
      .text('Feb. 28');

    bodyLines.forEach((lineText) => {
      eventText.append('tspan')
        .attr('dy', '1.3em')
        .attr('class', 'event-label')
        .text(lineText);
    });

    // Widest rendered line, used to test whether the block clears the axes.
    const widestLine = () => {
      let w = 0;
      eventText.selectAll('tspan').each(function measure() {
        w = Math.max(w, this.getComputedTextLength());
      });
      return w;
    };

    const place = (side) => {
      const x = side === 'start' ? eventX + gapFromLine : eventX - gapFromLine;
      eventText.attr('text-anchor', side).attr('x', x);
      eventText.selectAll('tspan').attr('x', x);
    };

    const fits = (side, w) => (side === 'start'
      ? eventX + gapFromLine + w <= chartWidth - edgePad
      : eventX - gapFromLine - w >= edgePad);

    // Start on the roomier side, flip if it overflows, and if neither side
    // clears an axis, keep just the bold date.
    let anchor = (chartWidth - eventX) >= eventX ? 'start' : 'end';
    place(anchor);

    if (!fits(anchor, widestLine())) {
      const other = anchor === 'start' ? 'end' : 'start';
      place(other);
      if (fits(other, widestLine())) {
        anchor = other;
      } else {
        eventText.selectAll('tspan.event-label').remove();
        anchor = (chartWidth - eventX) >= eventX ? 'start' : 'end';
        place(anchor);
        if (!fits(anchor, widestLine())) place(anchor === 'start' ? 'end' : 'start');
      }
    }
  }

  const line = g.append('g')
    .attr('class', 'line');

  line.append('path')
    .datum(combinedData)
    .attr('stroke', '#f18851')
    .attr('stroke-width', isMobile ? 1.5 : 2)
    .attr('fill', 'none')
    .attr('d', path);

  if (currentData) {
    let displayPrice;
    let displayChange;
    let displayPctChange;
    if (livePrice) {
      displayPrice = livePrice.currentPrice;
      if (livePrice.frozen) {
        displayChange = livePrice.change;
        displayPctChange = livePrice.pctChange;
      } else {
        const base = liveChangeBase();
        displayChange = livePrice.currentPrice - base;
        displayPctChange = displayChange / base;
      }
    } else {
      displayPrice = currentData.close;
      displayChange = currentData.change;
      displayPctChange = currentData.percentChange;
    }

    const changeColor = displayChange >= 0 ? '#53A796' : '#c52622';
    const changeSymbol = displayChange >= 0 ? '+' : '';

    const cx = xScale(currentData.date);
    const cy = yScale(displayPrice);

    const currentPoint = g.append('g')
      .attr('class', 'current-point')
      .attr('transform', `translate(${cx},${cy})`);

    // Liveness pulse: a disc at the dot's radius, behind the dot, animated out.
    // Matches the dot's up/down color rather than the series orange. Suppressed
    // when frozen, since the chart is no longer live.
    if (!FROZEN) {
      currentPoint.append('circle')
        .attr('class', 'pulse')
        .attr('r', 4)
        .attr('fill', changeColor);
    }

    currentPoint.append('circle')
      .attr('r', '4')
      .attr('fill', changeColor)
      .attr('stroke', 'white')
      .attr('stroke-width', '2');

    const annotation = g.append('g')
      .attr('class', 'annotation')
      .attr('transform', `translate(${cx},${cy})`);

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-price')
      .text(`Latest: $${d3.format(',.2f')(displayPrice)}`);

    const changeSign = displayChange >= 0 ? '+$' : '-$';
    const changeAbs = Math.abs(displayChange);

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-change')
      .attr('fill', changeColor)
      .text(`${changeSign}${d3.format('.2f')(changeAbs)} (${d3.format('.2%')(displayPctChange)})`)
      .attr('dy', '1.2em');

    const timestampText = livePrice && livePrice.lastUpdated
      ? formatLiveTimestamp(livePrice.lastUpdated)
      : (brentStats ? formatShortDate(brentStats.latestDate) : '');

    annotation.append('text')
      .attr('dx', '10px')
      .attr('class', 'annotation-time')
      .text(timestampText)
      .attr('dy', '2.4em');
  }

  window.pymChild.sendHeight();
}
